import { useEffect, useState } from 'react'
import { formatDateShort, getWeekday, isLessonInPast, uniqueLessonDates } from '@/lib/date-utils'
import { exclusionTitle } from '@/utils/exclusionHatch'
import { getStatusLabel } from '@/utils/statusHelpers'

interface Lesson {
  entry_id: string
  date: string
  time_slot_id: string
  time_slot_number: number
  start_time: string
  end_time: string
  subject_name: string | null
  room_name: string | null
  klass_name: string | null
  is_requestable: boolean
  substitution_excluded?: boolean
  existing_request_id: string | null
  existing_request_status: string | null
}

interface DayLessonsPanelProps {
  date: string
  lessons: Lesson[]
  selectedLessons: { entryId: string; date: string; timeSlotId: string; startTime?: string }[]
  onLessonToggle: (lesson: Lesson) => void
  onSelectAll: () => void
  onDeselectAll: () => void
  onSelectDay?: (date: string) => void
  onDeselectDay?: (date: string) => void

  // Time Picker v2
  selectedDates?: string[]
  multiDayData?: Record<string, { date: string; lessons: Lesson[]; is_school_day: boolean }>
}

export function DayLessonsPanel({
  date,
  lessons,
  selectedLessons,
  onLessonToggle,
  onSelectAll,
  onDeselectAll,
  onSelectDay,
  onDeselectDay,
  selectedDates,
  multiDayData,
}: DayLessonsPanelProps) {
  const selectedIds = new Set(selectedLessons.map((l) => `${l.entryId}|${l.date}|${l.startTime || ''}`))
  const selectedLessonDates = uniqueLessonDates(selectedLessons)
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set())
  const isMultiDay = selectedDates && selectedDates.length > 1
  const spanKey = isMultiDay ? selectedDates.join('|') : ''

  useEffect(() => {
    if (spanKey) setExpandedDays(new Set())
  }, [spanKey])

  const toggleDay = (day: string) => {
    setExpandedDays(prev => {
      const next = new Set(prev)
      if (next.has(day)) next.delete(day)
      else next.add(day)
      return next
    })
  }

  // Single day mode (existing behavior)
  if (!isMultiDay) {
    if (lessons.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
          Keine Lektionen an diesem Tag
        </div>
      )
    }

    return (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            Lektionen am {new Date(date).toLocaleDateString('de-DE')}
          </h2>
          <div className="flex gap-2">
            <button
              onClick={onSelectAll}
              className="px-2 py-1 text-xs border rounded hover:bg-gray-50"
            >
              Alle auswählen
            </button>
            <button
              onClick={onDeselectAll}
              className="px-2 py-1 text-xs border rounded hover:bg-gray-50"
            >
              Auswahl aufheben
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {lessons.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')).map((lesson) => {
            const isSelected = selectedIds.has(`${lesson.entry_id}|${lesson.date}|${lesson.start_time || ''}`)
            const isPast = isLessonInPast(lesson.date, lesson.end_time)
            const isExcluded = !!lesson.substitution_excluded && !isPast
            const isRequested = !lesson.is_requestable && !isPast && !isExcluded
            const isBlocked = isPast || isRequested || isExcluded

            return (
              <button
                key={`${lesson.entry_id}|${lesson.date}|${lesson.start_time || ''}`}
                onClick={() => !isBlocked && onLessonToggle(lesson)}
                disabled={isBlocked}
                className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                  isExcluded
                    ? 'border-red-300 cursor-not-allowed'
                    : isBlocked
                    ? 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed'
                    : isSelected
                      ? 'border-blue-300 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      isSelected
                        ? 'bg-blue-500 border-blue-500'
                        : 'border-gray-300'
                    }`}
                  >
                    {isSelected && <span className="text-white text-xs">✓</span>}
                  </div>
                  <div className="text-left">
                    <div className="font-medium">
                      {lesson.subject_name || 'Unbekannt'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {lesson.start_time} - {lesson.end_time}
                      {lesson.klass_name && ` · ${lesson.klass_name}`}
                      {lesson.room_name && ` · ${lesson.room_name}`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isPast && (
                    <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">
                      Vergangen
                    </span>
                  )}
                  {isExcluded && (
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                      {exclusionTitle}
                    </span>
                  )}
                  {isRequested && (
                    <span
                      className={
                        lesson.existing_request_status
                          ? "text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded"
                          : "text-xs bg-red-100 text-red-700 px-2 py-1 rounded"
                      }
                    >
                      {lesson.existing_request_status
                        ? getStatusLabel(lesson.existing_request_status)
                        : 'Bereits angefragt'}
                    </span>
                  )}
                  {lesson.is_requestable && !isPast && !isExcluded && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      Verfügbar
                    </span>
                  )}
                </div>
              </button>
            )
          })}
        </div>

        {selectedLessons.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
            {selectedLessons.length} Lektion{selectedLessons.length !== 1 ? 'en' : ''} ausgewählt
          </div>
        )}
      </div>
    )
  }

  // Multi-day mode
  const totalLessons = selectedDates.reduce((sum, d) => {
    const dayData = multiDayData?.[d]
    return sum + (dayData?.lessons?.filter(l => l.is_requestable).length || 0)
  }, 0)

  const daysWithLessons = selectedDates.filter(d => {
    const dayData = multiDayData?.[d]
    const requestableLessons = dayData?.lessons?.filter(l => l.is_requestable) || []
    return requestableLessons.length > 0
  })

  if (daysWithLessons.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
        Keine Lektionen im gewählten Zeitraum
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">
          Lektionen: {daysWithLessons.length} Tage
        </h2>
      </div>

      <div className="bg-gray-50 rounded-lg p-3 mb-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Gesamten Zeitraum</span>
          <div className="flex gap-2">
            <button
              onClick={onSelectAll}
              className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Alle
            </button>
            <button
              onClick={onDeselectAll}
              className="px-3 py-1.5 text-xs font-medium bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
            >
              Keine
            </button>
          </div>
        </div>
        <div className="mt-2 text-sm text-gray-600">
          {selectedLessons.length} von {totalLessons} Lektionen ausgewählt
        </div>
      </div>

      <div className="space-y-3">
        {daysWithLessons.map(d => {
          const dayData = multiDayData?.[d]
          const dayLessons = dayData?.lessons || []
          const requestableLessons = dayLessons.filter(l => l.is_requestable)
          const selectedInDay = requestableLessons.filter(l => selectedIds.has(`${l.entry_id}|${l.date}|${l.start_time || ''}`))
          const isExpanded = expandedDays.has(d)
          const weekday = getWeekday(d)
          const dateShort = formatDateShort(d)

          return (
            <div key={d} className="border rounded-lg">
              <div
                className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
              >
                <button
                  onClick={() => toggleDay(d)}
                  className="flex items-center gap-2 flex-1"
                >
                  <span className={`text-sm ${isExpanded ? 'rotate-90' : ''} transition-transform`}>▶</span>
                  <span className="font-medium">{weekday} {dateShort}</span>
                  <span className="text-sm text-gray-500">
                    ({selectedInDay.length}/{requestableLessons.length})
                  </span>
                </button>
                {requestableLessons.length > 0 && (
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        onSelectDay?.(d)
                      }}
                      className={`px-2 py-1 text-xs border rounded transition-colors ${
                        selectedInDay.length === requestableLessons.length
                          ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400'
                          : 'hover:bg-blue-50 hover:border-blue-300'
                      }`}
                      disabled={selectedInDay.length === requestableLessons.length}
                    >
                      Alle
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        onDeselectDay?.(d)
                      }}
                      className={`px-2 py-1 text-xs border rounded transition-colors ${
                        selectedInDay.length === 0
                          ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400'
                          : 'hover:bg-gray-50'
                      }`}
                      disabled={selectedInDay.length === 0}
                    >
                      Keine
                    </button>
                  </div>
                )}
              </div>

              {isExpanded && (
                <div className="px-3 pb-3 space-y-2">
                  {requestableLessons.map((lesson) => {
                    const isSelected = selectedIds.has(`${lesson.entry_id}|${lesson.date}|${lesson.start_time || ''}`)

                    return (
                      <button
                        key={`${lesson.entry_id}|${lesson.date}|${lesson.start_time || ''}`}
                        onClick={() => onLessonToggle(lesson)}
                        className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                          isSelected
                            ? 'border-blue-300 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              isSelected
                                ? 'bg-blue-500 border-blue-500'
                                : 'border-gray-300'
                            }`}
                          >
                            {isSelected && <span className="text-white text-xs">✓</span>}
                          </div>
                          <div className="text-left">
                            <div className="font-medium">
                              {lesson.subject_name || 'Unbekannt'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {lesson.start_time} - {lesson.end_time}
                              {lesson.klass_name && ` · ${lesson.klass_name}`}
                              {lesson.room_name && ` · ${lesson.room_name}`}
                            </div>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {selectedLessons.length > 0 && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
          {selectedLessons.length} Lektion{selectedLessons.length !== 1 ? 'en' : ''} über {selectedLessonDates.length} Tage ausgewählt
        </div>
      )}
    </div>
  )
}
